/** Instances in one spatial cell share a batch, so culling still drops far-away batches (world units). */
export const HS_INSTANCING_SPATIAL_CELL_SIZE: number = 100;
export const HS_INSTANCING_MAX_OBJECTS_PER_BATCH: number = 256;
export const HS_INSTANCING_MAX_TRIANGLES_PER_BATCH: number = 250000;

/** What makes two drawn meshes interchangeable instances. */
export interface HsInstancingCandidate {
  geometryId: string;
  /** Material id, color and alpha; instances of one batch must look the same. */
  materialKey: string;
  /** Column-major world matrix; its translation picks the spatial cell. */
  worldMatrix: ArrayLike<number>;
}

/**
 * Groups render keys whose meshes share geometry, material and spatial cell. `describe` returns null for
 * keys that cannot be instanced (textured, transparent, missing).
 */
export function hsGroupInstancingCandidates(
  renderKeys: Iterable<string>,
  describe: (renderKey: string) => HsInstancingCandidate | null,
  cellSize: number = HS_INSTANCING_SPATIAL_CELL_SIZE
): Map<string, string[]> {
  const groups: Map<string, string[]> = new Map<string, string[]>();
  for (const renderKey of renderKeys) {
    const candidate: HsInstancingCandidate | null = describe(renderKey);
    if (candidate === null) {
      continue;
    }
    const position: ArrayLike<number> = candidate.worldMatrix;
    const cellX: number = Math.floor((position[12] ?? 0) / cellSize);
    const cellY: number = Math.floor((position[13] ?? 0) / cellSize);
    const cellZ: number = Math.floor((position[14] ?? 0) / cellSize);
    const groupKey: string = `${candidate.geometryId}|${candidate.materialKey}|${cellX}|${cellY}|${cellZ}`;
    const group: string[] = groups.get(groupKey) ?? [];
    group.push(renderKey);
    groups.set(groupKey, group);
  }
  return groups;
}

/** Splits a group into batches of at most the object and triangle limits. */
export function hsSplitInstancingBatches(
  renderKeys: readonly string[],
  trianglesPerInstance: number,
  maxObjects: number = HS_INSTANCING_MAX_OBJECTS_PER_BATCH,
  maxTriangles: number = HS_INSTANCING_MAX_TRIANGLES_PER_BATCH
): string[][] {
  const chunks: string[][] = [];
  let current: string[] = [];
  let triangles: number = 0;
  for (const renderKey of renderKeys) {
    if (current.length >= maxObjects || (current.length > 0 && triangles + trianglesPerInstance > maxTriangles)) {
      chunks.push(current);
      current = [];
      triangles = 0;
    }
    current.push(renderKey);
    triangles += trianglesPerInstance;
  }
  if (current.length > 0) {
    chunks.push(current);
  }
  return chunks;
}
